import { createSelector } from 'reselect';
import { Map, List, getIn } from 'immutable';

import { UserFeatureFlags } from 'enums/user-membership-features';
import { denormalizeModelList } from '../utils/Normalizer';
import { makeSelectFeatureFlagActive } from '../featureFlags/selectors';
import { makeSelectProfileFundingSearchId } from '../fundingSearchProfile/selectors';

const sortFundingSearchByTeams = (list) =>
  list.sort((fsA, fsB) => {
    // Sort by manager
    if (fsA.isManager || fsB.isManager) {
      return fsA.isManager && fsB.isManager
        ? fsB.created - fsA.created
        : fsB.isManager - fsA.isManager;
    }

    // Sort by follower
    if (fsA.isFollower || fsB.isFollower) {
      return fsA.isFollower && fsB.isFollower
        ? fsB.created - fsA.created
        : fsB.isFollower - fsA.isFollower;
    }

    // Sort by team search
    if (fsA.isTeamSearch || fsB.isTeamSearch) {
      return fsA.isTeamSearch && fsB.isTeamSearch
        ? fsB.created - fsA.created
        : fsB.isTeamSearch - fsA.isTeamSearch;
    }

    // Sort by created
    return fsB.created - fsA.created;
  });

const selectFundingSearch = (state) => state.get('fundingSearch');
const selectFundingSearchIdFromUrl = (_, props) =>
  parseInt(getIn(props, ['match', 'params', 'fundingSearchId'], ''), 10);

const makeSelectDueActivitiesCountState = () =>
  createSelector(selectFundingSearch, (state) =>
    state.get('fundingSearchDueActivitiesCount', Map()),
  );

const makeSelectFetchingOfferMatchIds = () =>
  createSelector(selectFundingSearch, (state) => state.get('fetchingOfferMatchIds', List()));

const makeSelectFundingMatchNotifications = () =>
  createSelector(selectFundingSearch, (state) => state.get('notifications', List()));

const makeSelectTradeHistoryAccountingSolution = () =>
  createSelector(selectFundingSearch, (state) => state.get('accountingSolution', ''));

const makeSelectById = () =>
  createSelector(selectFundingSearch, (state) => state.get('byId', Map()));

const makeSelectByType = (type) =>
  createSelector(selectFundingSearch, (state) => state.get(type, Map()));

const makeSelectListByType = (type) =>
  createSelector(
    makeSelectById(),
    makeSelectByType(type),
    makeSelectFeatureFlagActive(UserFeatureFlags.teams),
    (byId, state, isTeamsActive) => {
      const list = denormalizeModelList(byId, state.get('ids', List()));

      return isTeamsActive && (type === 'active' || type === 'completed')
        ? sortFundingSearchByTeams(list)
        : list;
    },
  );

const makeSelectFirstByType = (type) =>
  createSelector(makeSelectListByType(type), (list) => list.get(0) || null);

const makeSelectListByTypeWithDueActivities = (type) =>
  createSelector(
    makeSelectListByType(type),
    makeSelectDueActivitiesCountState(),
    (list, counters) =>
      list.map((elem) => elem.set('dueCount', counters.get(elem.get('id', 0).toString(), 0))),
  );

const makeSelectLoadingByType = (type) =>
  createSelector(makeSelectByType(type), (state) => state.getIn(['network', 'loading']));

const makeSelectErrorByType = (type) =>
  createSelector(makeSelectByType(type), (state) => state.getIn(['network', 'error']));

const makeSelectFundingSearchFromUrl = () =>
  createSelector(makeSelectById(), selectFundingSearchIdFromUrl, (byId, id) => byId.get(id));

const makeSelectFundingSearchBySearchProfileId = () =>
  createSelector(makeSelectById(), makeSelectProfileFundingSearchId(), (byId, id) => byId.get(id));

const makeSelectClientIdBySearchProfileId = () =>
  createSelector(
    makeSelectFundingSearchBySearchProfileId(),
    (fundingSearch) => fundingSearch?.getIn(['client', 'id']) ?? 0,
  );

const makeSelectFundingSearchPageLoading = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['fundingSearchPage', 'network', 'loading']),
  );

const makeSelectFundingSearchPageError = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['fundingSearchPage', 'network', 'error']),
  );

const makeSelectCancelFundingSearchModalState = () =>
  createSelector(selectFundingSearch, (state) => state.getIn(['cancelFundingSearch', 'modalOpen']));

const makeSelectDeleteDirectorConfirmationModalState = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['deleteDirectorConfirmation', 'modalOpen']),
  );

const makeSelectDeleteDirectorConfirmationPromise = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['deleteDirectorConfirmation', 'deletePromise']),
  );

const makeSelectFundingSearchToCancel = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['cancelFundingSearch', 'fundingSearch']),
  );

const makeSelectDeleteBusinessProfileModalState = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['deleteBusinessProfile', 'modalOpen']),
  );

const makeSelectBusinessProfileToDelete = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['deleteBusinessProfile', 'fundingSearch']),
  );
const makeSelectRequestFundingSearchHelpModalState = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['requestFundingSearchHelp', 'modalOpen']),
  );

const makeSelectRequestFundingSearchHelp = () =>
  createSelector(selectFundingSearch, (state) =>
    state.getIn(['requestFundingSearchHelp', 'fundingSearch']),
  );

export {
  selectFundingSearch,
  makeSelectById,
  makeSelectByType,
  makeSelectFirstByType,
  makeSelectListByType,
  makeSelectLoadingByType,
  makeSelectErrorByType,
  makeSelectFetchingOfferMatchIds,
  makeSelectFundingMatchNotifications,
  makeSelectListByTypeWithDueActivities,
  makeSelectFundingSearchFromUrl,
  makeSelectFundingSearchBySearchProfileId,
  makeSelectClientIdBySearchProfileId,
  makeSelectFundingSearchToCancel,
  makeSelectFundingSearchPageError,
  makeSelectFundingSearchPageLoading,
  makeSelectCancelFundingSearchModalState,
  makeSelectTradeHistoryAccountingSolution,
  makeSelectDeleteBusinessProfileModalState,
  makeSelectBusinessProfileToDelete,
  makeSelectRequestFundingSearchHelpModalState,
  makeSelectRequestFundingSearchHelp,
  makeSelectDeleteDirectorConfirmationModalState,
  makeSelectDeleteDirectorConfirmationPromise,
};

export * from './searchFollowers/selectors';
export * from './contactDetails/selectors';
export * from './privacyToggle/selectors';
export * from './match/selectors';
export * from './offers/selectors';
export * from './offersHistory/selectors';
